import { useMemo, useState } from "react";
import { TeamTreeSidebar } from "@/components/teams/team-tree-sidebar";
import { TeamCard } from "@/components/teams/team-card";
import { TeamForm } from "@/components/teams/team-form";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { usePageTitle } from "@/hooks/use-page-title";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTeamTree } from "@/hooks/queries/use-teams";
import { countEpicOwnerTeams, countTeamsInTree } from "@/lib/team-hierarchy";
import { FolderTree, Plus } from "lucide-react";
import { PageInfoButton } from "@/components/common/page-info-button";
import { HELP_IDS } from "@/lib/help-ids";
import { WriteGuard } from "@/components/billing/write-guard";

export function TeamsPage() {
  usePageTitle("Teams & Products");
  const isMobile = useIsMobile();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { data: tree, isLoading } = useTeamTree();

  const totalTeams = useMemo(() => countTeamsInTree(tree ?? []), [tree]);
  const epicOwnerTeams = useMemo(() => countEpicOwnerTeams(tree ?? []), [tree]);

  const disciplineTeams = useMemo(() => (tree ?? []).filter((t) => t.level !== "product"), [tree]);
  const productTeams = useMemo(() => (tree ?? []).filter((t) => t.level === "product"), [tree]);

  return (
    <div className="pt-4">
      <div className="grid gap-4 lg:grid-cols-[320px,minmax(0,1fr)]">
        <div className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
          <TeamTreeSidebar />
        </div>

        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-4 md:p-6">
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <h1 className="text-xl font-semibold" data-help-id={HELP_IDS.teams.title}>Teams & Products</h1>
                  <PageInfoButton title="Teams & Products" />
                </div>
                <p className="max-w-2xl text-sm text-muted-foreground">
                  Navigate the full organization tree, inspect parent teams, and jump into discipline teams that own epics and requests.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {isMobile ? (
                  <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm">
                        <FolderTree className="mr-2 h-4 w-4" />
                        Browse hierarchy
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="border-r p-0">
                      <SheetHeader className="border-b border-white/[0.08]">
                        <SheetTitle>Team hierarchy</SheetTitle>
                      </SheetHeader>
                      <div className="p-4">
                        <TeamTreeSidebar onNavigate={() => setIsSidebarOpen(false)} />
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : null}
                <WriteGuard>
                  <Button
                    onClick={() => {
                      setIsFormOpen(true);
                    }}
                    data-help-id={HELP_IDS.teams.newTeamButton}
                    size={isMobile ? "sm" : "default"}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New Team
                  </Button>
                </WriteGuard>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Teams</p>
                <p className="mt-2 text-3xl font-semibold text-foreground">{isLoading ? "—" : totalTeams}</p>
                <p className="mt-1 text-sm text-muted-foreground">Across all visible levels in the hierarchy</p>
              </div>
              <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Epic owner teams</p>
                <p className="mt-2 text-3xl font-semibold text-foreground">{isLoading ? "—" : epicOwnerTeams}</p>
                <p className="mt-1 text-sm text-muted-foreground">Discipline teams that can own epics and requests</p>
              </div>
              <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Navigation</p>
                <p className="mt-2 text-lg font-semibold text-foreground">Select a team</p>
                <p className="mt-1 text-sm text-muted-foreground">Use the hierarchy sidebar to drill into parent teams, products, and leaf disciplines.</p>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-4 md:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Teams</h2>
                <p className="text-sm text-muted-foreground">Discipline teams and their parent hierarchy. Open one to browse its descendants and team details.</p>
              </div>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-[210px] animate-pulse rounded-2xl bg-muted/60" />
                ))}
              </div>
            ) : !disciplineTeams.length ? (
              <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-10 text-center text-muted-foreground">
                No teams yet. Create your first team to start building the hierarchy.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-help-id={HELP_IDS.teams.cardGrid}>
                {disciplineTeams.map((team) => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </div>
            )}
          </div>

          {!isLoading && productTeams.length > 0 && (
            <div className="glass-card rounded-2xl p-4 md:p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">Products</h2>
                  <p className="text-sm text-muted-foreground">Product-level teams. Each product inherits its parent discipline's workflow and members.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {productTeams.map((team) => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <TeamForm open={isFormOpen} onOpenChange={setIsFormOpen} />
    </div>
  );
}
